import React, { useState, useEffect, useRef } from "react";
import {
  IconButton,
  Tooltip,
  Box,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";

// Utility function to detect iOS
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Utility function to detect if running in HTTPS
const isSecureContext = () => {
  return window.location.protocol === 'https:' || 
         window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1';
};

const SpeechToText = ({
  onTranscript,
  disabled = false,
  language = "en-US",
  continuous = true,
  interimResults = true,
  showInterimText = false,
  onListeningStart,
  onListeningEnd,
  onSpeechError,
}) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [hasUserGesture, setHasUserGesture] = useState(false);
  const recognitionRef = useRef(null);
  const shouldRestartRef = useRef(false); // Flag to control auto-restart
  const restartTimeoutRef = useRef(null);
  const isIOSDevice = useRef(isIOS());
  const hasSecureContext = useRef(isSecureContext());
  // Stable refs for values used inside the recognition effect — prevents
  // re-initializing the entire SpeechRecognition instance when these change
  const onTranscriptRef = useRef(onTranscript);
  const disabledRef = useRef(disabled);
  const onListeningStartRef = useRef(onListeningStart);
  const onListeningEndRef = useRef(onListeningEnd);
  const onSpeechErrorRef = useRef(onSpeechError);
  onTranscriptRef.current = onTranscript;
  disabledRef.current = disabled;
  onListeningStartRef.current = onListeningStart;
  onListeningEndRef.current = onListeningEnd;
  onSpeechErrorRef.current = onSpeechError;

  useEffect(() => {
    // Check if speech recognition is supported
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    // Check for support and secure context
    if (!SpeechRecognition) {
      setIsSupported(false);
      setError("Speech recognition is not supported in this browser.");
      return;
    }

    if (!hasSecureContext.current) {
      setIsSupported(false);
      setError("Speech recognition requires a secure connection (HTTPS).");
      return;
    }

    setIsSupported(true);

    try {
      // Initialize speech recognition
      const recognition = new SpeechRecognition();
      
      // NOTE: on iOS this instance is only a placeholder for stopListening to
      // hold before the first tap. startListening() builds a fresh instance per
      // tap (see the iOS branch there) and that is where the settings which
      // actually apply on iOS are set. Do not tune iOS behaviour here.
      recognition.continuous = continuous;
      recognition.interimResults = interimResults;

      recognition.lang = language;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setIsListening(true);
        setError("");
        setInterimTranscript("");
        setHasUserGesture(true);
        console.log("Speech recognition started");
        onListeningStartRef.current?.();
      };

      recognition.onresult = (event) => {
        let finalTranscript = "";
        let interim = "";

        console.log('[SpeechToText] onresult event:', event.results.length, 'results');

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          const isFinal = event.results[i].isFinal;

          console.log(`[SpeechToText] Result ${i}: "${transcript}", isFinal: ${isFinal}`);

          if (isFinal) {
            finalTranscript += transcript;
          } else {
            interim += transcript;
          }
        }

        // Store interim results for Safari (both iOS and macOS) to send on speech end
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if ((isIOSDevice.current || isSafari) && interim && !finalTranscript) {
          console.log('[SpeechToText] Safari: Storing interim result for later:', interim);
          // Stash on this instance, not recognitionRef.current — the ref is
          // reassigned when this effect re-runs, and an aborted instance's
          // onend would otherwise read or clear its successor's slot.
          recognition._latestInterim = interim;
          interim = ""; // Clear interim so it doesn't display
        }

        // Only set interim transcript if showInterimText is enabled and not iOS
        if (showInterimText && !isIOSDevice.current) {
          // Limit to last 30 characters
          const truncatedInterim =
            interim.length > 30 ? "..." + interim.slice(-30) : interim;
          setInterimTranscript(truncatedInterim);
        }

        if (finalTranscript) {
          console.log('[SpeechToText] Sending final transcript:', finalTranscript);
          onTranscriptRef.current(finalTranscript);
          if (showInterimText) {
            setInterimTranscript("");
          }

          // On iOS, automatically stop after getting result
          if (isIOSDevice.current) {
            shouldRestartRef.current = false;
            setIsListening(false);
          }

          // Clear any stored interim for Safari since we got a final result
          const isSafariCleanup = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
          if (isIOSDevice.current || isSafariCleanup) {
            recognition._latestInterim = null;
          }
        } else {
          console.log('[SpeechToText] No final transcript to send');
        }
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);

        // Don't stop listening for certain errors, just log them (not on iOS)
        if (event.error === "no-speech" && shouldRestartRef.current && !isIOSDevice.current) {
          console.log("No speech detected, but continuing to listen...");
          return; // Don't set error or stop listening
        }

        setIsListening(false);
        shouldRestartRef.current = false;
        onSpeechErrorRef.current?.(event.error);

        switch (event.error) {
          case "audio-capture":
            setError("Microphone not accessible. Please check permissions.");
            break;
          case "not-allowed":
            if (isIOSDevice.current) {
              setError("Microphone access denied. Please allow microphone access in Safari settings and reload the page.");
            } else {
              setError("Microphone access denied. Please allow microphone access.");
            }
            break;
          case "network":
            setError("Network error occurred during speech recognition.");
            break;
          case "aborted":
            // Don't show error for manual abort
            break;
          case "no-speech":
            if (isIOSDevice.current) {
              setError("No speech detected. Please speak clearly and try again.");
            }
            break;
          default:
            if (event.error !== "no-speech") {
              setError(`Speech recognition error: ${event.error}`);
            }
        }
      };

      recognition.onend = () => {
        console.log("Speech recognition ended");

        // Send stored interim result for Safari browsers if no final result was sent
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if ((isIOSDevice.current || isSafari) && recognition._latestInterim) {
          console.log('[SpeechToText] Safari: Sending stored interim as final on end:', recognition._latestInterim);
          onTranscriptRef.current(recognition._latestInterim);
          recognition._latestInterim = null; // Clear it
        }

        // Auto-restart if we should keep listening and not manually stopped (disabled on iOS and Safari)
        if (shouldRestartRef.current && !disabledRef.current && !isIOSDevice.current && !isSafari) {
          console.log("Auto-restarting speech recognition...");
          // Small delay before restarting to prevent rapid cycling
          restartTimeoutRef.current = setTimeout(() => {
            if (shouldRestartRef.current && recognitionRef.current) {
              try {
                recognitionRef.current.start();
              } catch (err) {
                console.error("Error restarting speech recognition:", err);
                setIsListening(false);
                shouldRestartRef.current = false;
              }
            }
          }, 100);
        } else {
          setIsListening(false);
          shouldRestartRef.current = false;
          if (showInterimText) {
            setInterimTranscript("");
          }
          onListeningEndRef.current?.();
        }
      };

      recognitionRef.current = recognition;
    } catch (initError) {
      console.error("Error initializing speech recognition:", initError);
      setIsSupported(false);
      setError("Failed to initialize speech recognition. Please try refreshing the page.");
    }

    return () => {
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      if (recognitionRef.current) {
        shouldRestartRef.current = false;
        recognitionRef.current.abort();
      }
    };
  // Note: onTranscript and disabled are accessed via refs (onTranscriptRef, disabledRef)
  // so they don't need to be in the dependency array. This prevents the recognition
  // instance from being destroyed/recreated on every render or loading state change.
  }, [
    language,
    continuous,
    interimResults,
    showInterimText,
  ]);

  const startListening = () => {
    if (!isSupported || disabled) return;

    try {
      // Reset error state
      setError("");
      
      // For iOS, create a fresh instance each time to avoid permission issues
      if (isIOSDevice.current) {
        console.log('[SpeechToText] iOS: Creating fresh recognition instance');
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        
        // iOS-specific settings. These are the ones that actually apply on iOS.
        // continuous is always false: WebKit does not hold a continuous session
        // reliably, so recognition ends after each utterance regardless of the
        // `continuous` prop. interimResults must be true, because iOS often
        // never emits an isFinal result and the last interim is all we get.
        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.lang = language;
        recognition.maxAlternatives = 1;
        
        // Set up event handlers
        recognition.onstart = () => {
          setIsListening(true);
          setError("");
          setInterimTranscript("");
          setHasUserGesture(true);
          console.log("Speech recognition started");
        };

        recognition.onresult = (event) => {
          console.log('[SpeechToText] iOS onresult fired with', event.results.length, 'results');
          let finalTranscript = "";
          let latestInterim = "";

          // Process all results to get the most recent/complete transcript
          for (let i = 0; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            const isFinal = event.results[i].isFinal;
            
            console.log(`[SpeechToText] iOS Result ${i}: "${transcript}", isFinal: ${isFinal}`);

            if (isFinal) {
              finalTranscript += transcript;
            } else {
              // Keep only the latest interim result (not cumulative)
              latestInterim = transcript;
            }
          }

          // Only send final results immediately, store interim for later.
          // Stash on `recognition` (this instance), NOT on recognitionRef.current:
          // each tap creates a fresh instance and reassigns the ref, so going
          // through the ref lets a stopped instance read or clobber its
          // successor's slot and silently lose the utterance.
          if (finalTranscript) {
            console.log('[SpeechToText] iOS: Sending FINAL transcript:', finalTranscript);
            recognition._latestInterim = null;
            onTranscriptRef.current(finalTranscript);
            setIsListening(false);
          } else if (latestInterim) {
            console.log('[SpeechToText] iOS: Interim result (not sending yet):', latestInterim);
            recognition._latestInterim = latestInterim;
          }
        };

        recognition.onerror = (event) => {
          console.error("[SpeechToText] iOS speech recognition error:", event.error);
          setIsListening(false);
          if (event.error === 'not-allowed') {
            setError("Microphone access denied. Please allow microphone access.");
          }
        };

        recognition.onend = () => {
          console.log("[SpeechToText] iOS speech recognition ended");
          setIsListening(false);

          // iOS frequently never emits an isFinal result, so the last interim
          // IS the utterance. Read it off this instance so a quick stop/restart
          // cannot drop it or steal the next instance's value.
          if (recognition._latestInterim) {
            console.log('[SpeechToText] iOS: Sending stored interim as final:', recognition._latestInterim);
            onTranscriptRef.current(recognition._latestInterim);
            recognition._latestInterim = null;
          }
        };
        
        // Start recognition
        recognition.start();
        
        // Store reference for stopping
        recognitionRef.current = recognition;
        shouldRestartRef.current = false;
        
      } else {
        // Non-iOS behavior (existing code)
        if (!recognitionRef.current) return;
        
        // For iOS, ensure we have proper user gesture
        if (isIOSDevice.current && !hasUserGesture) {
          setHasUserGesture(true);
        }
        
        shouldRestartRef.current = true;
        recognitionRef.current.start();
      }
      
    } catch (err) {
      console.error("Error starting speech recognition:", err);
      
      if (err.name === 'InvalidStateError') {
        setError("Speech recognition is already running. Please wait a moment and try again.");
      } else if (err.name === 'NotAllowedError') {
        if (isIOSDevice.current) {
          setError("Microphone access denied. Please enable microphone access in Safari settings.");
        } else {
          setError("Microphone access denied. Please allow microphone access.");
        }
      } else {
        setError("Failed to start speech recognition. Please try again.");
      }
    }
  };

  const stopListening = () => {
    shouldRestartRef.current = false;
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
    }
    // Always attempt to stop if we have a recognition instance — don't rely on
    // isListening state which may already be false from a result handler
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // May throw if already stopped — that's fine
        console.log('[SpeechToText] stop() threw (already stopped):', e.message);
      }
    }
    setIsListening(false);
  };

  const handleToggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleCloseError = () => {
    setError("");
  };

  if (!isSupported) {
    const tooltipMessage = isIOSDevice.current 
      ? "Speech recognition requires iOS 14.3+ and HTTPS. Please use Safari or Chrome."
      : "Speech recognition not supported in this browser";
      
    return (
      <Tooltip title={tooltipMessage}>
        <span>
          <IconButton disabled>
            <MicOffIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {/* Show interim transcript on the LEFT side of mic (only if enabled) */}
      {showInterimText && isListening && interimTranscript && (
        <Typography
          variant="caption"
          sx={{
            color: "error.main",
            fontStyle: "italic",
            fontSize: "0.7rem", // Smaller text size
            maxWidth: 150,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            backgroundColor: "rgba(244, 67, 54, 0.1)",
            padding: "1px 4px",
            borderRadius: "3px",
          }}
        >
          {interimTranscript}
        </Typography>
      )}

      {/* Show listening indicator when no interim text (only if enabled) */}
      {showInterimText && isListening && !interimTranscript && (
        <Typography
          variant="caption"
          sx={{
            color: "error.main",
            fontStyle: "italic",
            fontSize: "0.7rem", // Smaller text size
            animation: "blink 1.5s infinite",
            backgroundColor: "rgba(244, 67, 54, 0.1)",
            padding: "1px 4px",
            borderRadius: "3px",
            "@keyframes blink": {
              "0%, 50%": { opacity: 1 },
              "51%, 100%": { opacity: 0.3 },
            },
          }}
        >
          Listening...
        </Typography>
      )}

      <Tooltip
        title={
          isIOSDevice.current 
            ? (isListening ? "Listening... (iOS: tap again to restart)" : "Tap to speak (iOS: one sentence at a time)")
            : (isListening ? "Click to stop listening" : "Click to start voice input")
        }
      >
        <IconButton
          onClick={handleToggleListening}
          disabled={disabled}
          color={isListening ? "error" : "default"}
          sx={{
            backgroundColor: isListening ? "error.light" : "transparent",
            boxShadow: isListening ? "0 0 20px rgba(244, 67, 54, 0.6)" : "none",
            animation: isListening ? "pulse 2s infinite" : "none",
            "&:hover": {
              backgroundColor: isListening ? "error.main" : "action.hover",
            },
            "@keyframes pulse": {
              "0%": {
                boxShadow: "0 0 0 0 rgba(244, 67, 54, 0.7)",
              },
              "70%": {
                boxShadow: "0 0 0 10px rgba(244, 67, 54, 0)",
              },
              "100%": {
                boxShadow: "0 0 0 0 rgba(244, 67, 54, 0)",
              },
            },
          }}
        >
          <MicIcon />
        </IconButton>
      </Tooltip>

      {/* Error snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SpeechToText;
